import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  AccountInfo,
  EventCallbackFunction,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, first, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import * as Sentry from '@sentry/angular';
import { OAuthService } from 'angular-oauth2-oidc';

import { Store } from '@ngxs/store';
import {
  GetCompany,
  GetFullProfile,
  ProfileState,
  SetSelfProfile,
  SetTwoFactorEnabled,
} from '@et/guards';
import { ProfileUserTypeEnum, SelfProfile } from '@et/typings';
import { ProfileV2ApiService } from '@et/api';
import { environment } from '@et/environment';
import { initTracking, setUser, setUserFromSelfProfile } from '@et/utils';
import { InactivityLogoutService } from '@et/shared';
import { AuthService } from '@et/auth';

interface BetterAuthIdTokenClaims {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  roles?: unknown;
}

@Component({
  selector: 'et-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private msalBroadcastService = inject(MsalBroadcastService);
  private msal = inject(MsalService);
  private permissionsService = inject(NgxPermissionsService);
  private store = inject(Store);
  private profileV2Api = inject(ProfileV2ApiService);

  title = 'escrowtab';
  private readonly _onDestroy$ = new Subject<void>();
  private router = inject(Router);
  private oauth = inject(OAuthService, { optional: true });
  private readonly inactivityLogout = inject(InactivityLogoutService);
  private authService = inject(AuthService);

  ngOnInit() {
    initTracking();
    if (environment.useBetterAuth) {
      this.bootstrapBetterAuth();
    } else {
      this.bootstrapMsal();
    }
  }

  /**
   * Better Auth bootstrap. Load `/self` when tokens are already present after
   * APP_INITIALIZER `tryLogin`, and again on `token_received` if AuthCallback
   * (or a later silent refresh) establishes tokens after this first check.
   */
  private bootstrapBetterAuth() {
    this.oauth?.events
      .pipe(
        filter((e) => e.type === 'token_received'),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => this.loadSelfProfileForBetterAuth());

    this.loadSelfProfileForBetterAuth();
  }

  private loadSelfProfileForBetterAuth() {
    // Prefer access-token validity for the guard, but the SPA sends the id_token as
    // Bearer (opaque access_token). Proceed when either is valid so /self is not skipped.
    if (!this.oauth?.hasValidAccessToken() && !this.oauth?.hasValidIdToken()) {
      return;
    }
    if (this.store.selectSnapshot(ProfileState.v2ProfileId)) {
      return;
    }
    this.profileV2Api.getSelfProfile().subscribe({
      next: (self) => {
        this.dispatchProfileLoaded(self, this.idTokenEmail());
        this.loadTwoFactorEnabledForBetterAuth();
      },
      error: (err) => {
        const email = this.idTokenEmail();
        if (email) {
          Sentry.setUser({ email });
        }
        Sentry.captureException(err, { tags: { area: 'oidc-bootstrap' } });
        // Clear the access/id/refresh tokens so the guard doesn't loop on a
        // token the API rejects. Passing `true` suppresses the
        // postLogoutRedirectUri redirect so we can navigate explicitly.
        this.oauth?.logOut(true);
        void this.router.navigateByUrl('/auth/login?error=profile_load');
      },
    });
  }

  /**
   * Reads Better Auth's own live `user.twoFactorEnabled` (distinct from
   * `SelfProfile.requiredMfaMethod`, Profile v2's policy) right after the profile
   * loads, so `mfaRequiredGuard` (@et/guards) can decide whether to block access
   * behind the mandatory Authenticator-app enrollment gate.
   */
  private loadTwoFactorEnabledForBetterAuth(): void {
    this.authService.getSession().subscribe((session) => {
      this.store.dispatch(
        new SetTwoFactorEnabled(session?.twoFactorEnabled ?? false),
      );
    });
  }

  /**
   * Legacy MSAL bootstrap.
   * `MsalRedirectComponent` was removed from the app's `bootstrap` array to prevent it
   * from intercepting the Better Auth callback URL. When `useBetterAuth=false` we therefore
   * call `handleRedirectObservable()` here to ensure B2C redirect responses (the `?code=…`
   * hash that B2C posts back) are still processed and the active account is set before the
   * `inProgress$` stream emits `None`.
   */
  private bootstrapMsal() {
    this.msal.handleRedirectObservable().subscribe();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => {
        const account = this.msal.instance.getActiveAccount();
        if (!account) {
          this.inactivityLogout.stop();
          return;
        }

        this.profileV2Api.getSelfProfile().subscribe({
          next: (self) => {
            this.dispatchProfileLoaded(self, account.username);
            this.setInactivityLogout();
            this.setSentryContext(account, self);
            setUserFromSelfProfile(self, account.username);
          },
          error: () => {
            this.initializeFromAccountClaims(account);
            setUser(account);
          },
        });
      });
    this.setMsalInstaceCallback();
  }

  /**
   * Single place that primes the profile store + permissions. `landingGuard` and
   * `ProfileResolver` both wait on `selfProfile.profileId && userType` — this dispatch
   * populates both atomically.
   */
  private dispatchProfileLoaded(self: SelfProfile, email: string | undefined) {
    const permission = ProfileUserTypeEnum[self.userType];
    if (permission) {
      this.permissionsService.addPermission(permission);
    }
    this.store.dispatch([
      new SetSelfProfile(self),
      ...(self.company?.companyId
        ? [new GetCompany(self.company.companyId)]
        : []),
      new GetFullProfile(self.profileId),
    ]);
    if (email) {
      Sentry.setUser({ email });
      Sentry.setContext('user_scope', {
        email,
        company_id: self.company?.companyId,
        is_company_admin: self.isCompanyAdmin,
        profile_id: self.profileId,
        user_type: ProfileUserTypeEnum[self.userType],
      });
    }
  }

  /** Pull the email out of the Better Auth ID token; null when no token is loaded. */
  private idTokenEmail(): string | undefined {
    const claims = this.oauth?.getIdentityClaims() as
      | BetterAuthIdTokenClaims
      | undefined;
    return claims?.email ?? undefined;
  }

  /**
   * Sets the active account after a successful login redirect
   */
  private setMsalInstaceCallback() {
    const callback = (event: {
      eventType: EventType;
      payload: { account: AccountInfo };
    }) => {
      // set active account after redirect
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload.account
      ) {
        const account = event.payload.account;
        this.msal.instance.setActiveAccount(account);

        this.msal.loginRedirect();
      }
    };
    this.msal.instance.addEventCallback(callback as EventCallbackFunction);
  }

  /**
   * Sets the Sentry context from `/self` (authorization) and MSAL account (authentication email).
   * Kept for the MSAL path; Better Auth path uses `dispatchProfileLoaded`'s inline Sentry call
   * since it already has the email from the ID token claims.
   */
  private setSentryContext(account: AccountInfo | null, self: SelfProfile) {
    const email = account?.username ?? self.email;
    Sentry.setUser({ email });
    Sentry.setContext('user_scope', {
      email,
      company_id: self.company?.companyId,
      is_company_admin: self.isCompanyAdmin,
      profile_id: self.profileId,
      user_type: ProfileUserTypeEnum[self.userType],
    });
  }

  /**
   * Fallback when `/self` is unavailable: initialize from AAD claims.
   */
  private initializeFromAccountClaims(account: AccountInfo) {
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    if (!claims) {
      return;
    }

    const permission = claims['extension_et_user_type'] as string | undefined;
    if (permission) {
      this.permissionsService.addPermission(permission);
    }

    const companyId = claims['extension_et_company_id'] as string | undefined;
    const profileId = claims['extension_et_profile_id'] as string | undefined;
    this.store.dispatch([
      ...(companyId ? [new GetCompany(companyId)] : []),
      ...(profileId ? [new GetFullProfile(profileId)] : []),
    ]);

    Sentry.setUser({ email: account.username });
    Sentry.setContext('user_scope', {
      email: account.username,
      company_id: companyId,
      is_company_admin: claims['extension_et_is_company_admin'],
      profile_id: profileId,
      user_type: permission,
    });
  }

  /**
   * Sets the inactivity logout to start when the company is loaded
   */
  private setInactivityLogout() {
    this.store
      .select(ProfileState.getCompany)
      .pipe(filter(Boolean), first())
      .subscribe(() => this.inactivityLogout.start());
  }

  ngOnDestroy(): void {
    this.inactivityLogout.stop();
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
